import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ShoppingCart, X } from 'lucide-react';
import { ROUTE_NAMES } from '@tsgecart/config';
import { formatPaise } from '../../../../lib/format.js';
import { useCart, useCartMutations } from '../hooks/use-cart.js';
import { CartLineItem } from './cart-line-item.js';
import type { CartItem } from '../types/cart.js';

export interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Slide-out mini-cart, triggered from the header's cart icon. Works for
 * both signed-in and signed-out (guest-cart) visitors -- it just renders
 * whatever `useCart()` returns, and that hook already resolves the right
 * identity (Clerk token vs. guest token) on its own.
 *
 * Accessible dialog pattern copied from `promo-popup.tsx` (itself
 * documented as mirroring `product-listing.tsx`'s filter drawer): `role="dialog"`,
 * `aria-modal`, Escape-to-close, a focus trap confined to the panel, and
 * focus returned to whatever triggered the drawer (the header's cart
 * button) on close.
 */
export function CartDrawer({ open, onClose }: CartDrawerProps) {
  const { data: cart, isLoading } = useCart();
  const { update, remove } = useCartMutations();

  const dialogRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();
    const container = dialogRef.current;

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !container) return;
      const items = Array.from(
        container.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, select'),
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previouslyFocused.current?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  const items = cart?.items ?? [];
  const itemCount = cart?.itemCount ?? 0;

  function handleQuantityChange(item: CartItem, quantity: number) {
    update.mutate({ itemId: item.id, input: { quantity } });
  }

  function handleRemove(item: CartItem) {
    remove.mutate(item.id);
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cart-drawer-title"
        className="relative flex h-full w-full max-w-sm flex-col bg-white shadow-xl"
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <p id="cart-drawer-title" className="flex items-center gap-2 text-base font-bold text-gray-900">
            <ShoppingCart className="h-5 w-5" aria-hidden="true" />
            Your cart{itemCount > 0 && ` (${itemCount})`}
          </p>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close cart"
            className="focus-visible:ring-brand flex h-8 w-8 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-gray-500">Loading your cart…</p>
          ) : items.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 py-12 text-center">
              <ShoppingCart className="h-10 w-10 text-gray-300" aria-hidden="true" />
              <p className="text-sm font-medium text-gray-900">Your cart is empty</p>
              <p className="text-xs text-gray-500">Add items to see them here.</p>
            </div>
          ) : (
            <ul>
              {items.map((item) => (
                <CartLineItem
                  key={item.id}
                  item={item}
                  onQuantityChange={handleQuantityChange}
                  onRemove={handleRemove}
                  isUpdating={update.isPending && update.variables?.itemId === item.id}
                  isRemoving={remove.isPending && remove.variables === item.id}
                />
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="space-y-3 border-t border-gray-100 px-5 py-4">
            <div className="flex items-center justify-between text-sm font-semibold text-gray-900">
              <span>Subtotal</span>
              <span>{formatPaise(cart?.subtotalPaise ?? 0)}</span>
            </div>
            <div className="flex flex-col gap-2">
              <Link
                to={ROUTE_NAMES.cart}
                onClick={onClose}
                className="focus-visible:ring-brand block w-full rounded-full border border-gray-200 px-4 py-2.5 text-center text-sm font-semibold text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2"
              >
                View cart
              </Link>
              <Link
                to={ROUTE_NAMES.checkout}
                onClick={onClose}
                className="bg-brand text-brand-foreground hover:bg-brand/90 focus-visible:ring-brand block w-full rounded-full px-4 py-2.5 text-center text-sm font-semibold focus-visible:outline-none focus-visible:ring-2"
              >
                Checkout
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
